import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Sparkles, Flame, Lock, ChevronRight, MessageCircle, Heart, Skull, Instagram } from 'lucide-react';

// TikTok icon (lucide doesn't ship one)
const TikTokIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.84a8.16 8.16 0 0 0 4.77 1.52V6.91a4.85 4.85 0 0 1-1.84-.22Z"/>
  </svg>
);

const SOCIALS = {
  instagram: 'https://www.instagram.com/animuschat/',
  tiktok: 'https://www.tiktok.com/@projectanimus',
};

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ai-persona-connect/artifacts/d3pkba7d_copy_8D43DC47-40F7-432E-B350-F206250169DA.png';

// Featured 8 — anime heavy-hitters + originals for pull rate
const FEATURED = [
  { id: '6a297667421a8b61cac78b85', name: 'Lucy', tag: 'Cyberpunk Netrunner', avatar: '/api/uploads/33ff74611fb7497082a06423dac6e288.jpg', accent: 'from-yellow-400 to-cyan-400', vibe: '⚡' },
  { id: '6a29435d5555f4683c6ec594', name: 'Makima', tag: 'Control Devil', avatar: '/api/uploads/b38d9317b9cb4980ac9edc3626f1847a.jpg', accent: 'from-red-500 to-orange-400', vibe: '🩸' },
  { id: '6a294c51fc5c45f39b595073', name: 'Esdeath', tag: 'Ice Queen General', avatar: '/api/uploads/c828ff1ff6214cebae1056ed65cb04bc.jpg', accent: 'from-cyan-400 to-blue-500', vibe: '❄️' },
  { id: '6a296ff4c8e411d6d1ee079c', name: 'Kobeni', tag: 'Anxious Devil Hunter', avatar: 'https://customer-assets.emergentagent.com/job_ai-persona-connect/artifacts/ocug9464_IMG_0183.jpeg', accent: 'from-pink-400 to-rose-400', vibe: '😭' },
  { id: '6a296ff4c8e411d6d1ee079d', name: 'Touka', tag: 'Ghoul Barista', avatar: '/api/uploads/fa87adbc0f78497893919dc5783e377c.jpg', accent: 'from-purple-500 to-fuchsia-500', vibe: '☕' },
  { id: '6a294e90fc5c45f39b595074', name: 'Echidna', tag: 'Witch of Greed', avatar: '/api/uploads/a0b696995cf24070bf1a9b5763012cda.jpg', accent: 'from-violet-500 to-pink-500', vibe: '🍵' },
  { id: '6a297667421a8b61cac78b87', name: 'The Void', tag: 'Eldritch Yandere', avatar: '/api/uploads/408ce5613f80431c993b57177a5f0aab.jpg', accent: 'from-slate-300 to-zinc-500', vibe: '👁️' },
  { id: '6a297667421a8b61cac78b88', name: 'Hina', tag: 'Devoted Classmate', avatar: '/api/uploads/d5534ac15b04418aaf39117f35af414d.jpg', accent: 'from-rose-400 to-pink-300', vibe: '💕' },
];

const SOCIAL_PROOF = [
  '"holy shit Makima is unhinged 😭"',
  '"finally an app that doesn\'t say no"',
  '"Kobeni stayed in character for 3 hours straight"',
  '"this beats every other AI chat app"',
  '"I forgot it was AI for a sec ngl"',
];

export default function Welcome() {
  const [proofIdx, setProofIdx] = useState(0);

  useEffect(() => {
    const i = setInterval(() => setProofIdx((p) => (p + 1) % SOCIAL_PROOF.length), 2800);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden" data-testid="welcome-page">
      {/* Animated background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-0">
        <div className="absolute top-[10%] -left-20 w-[500px] h-[500px] rounded-full bg-fuchsia-600/20 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[10%] -right-20 w-[600px] h-[600px] rounded-full bg-cyan-500/15 blur-[140px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[50%] left-[40%] w-[400px] h-[400px] rounded-full bg-yellow-400/10 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grain texture */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] -z-0" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
      }} />

      <div className="relative z-10">
        {/* Top nav */}
        <nav className="flex items-center justify-between px-5 sm:px-8 py-5 max-w-7xl mx-auto">
          <Link to="/welcome" className="flex items-center gap-2" data-testid="welcome-logo">
            <img src={LOGO_URL} alt="Animus" className="w-8 h-8 object-contain" />
            <span className="font-black text-xl tracking-tight font-['Unbounded']">Animus</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-semibold text-white/70 hover:text-white transition-colors"
            data-testid="welcome-nav-login"
          >
            Sign in
          </Link>
        </nav>

        {/* HERO */}
        <section className="px-5 sm:px-8 pt-8 sm:pt-12 pb-16 max-w-7xl mx-auto">
          {/* Tag pill */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] backdrop-blur-md">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                <span className="relative rounded-full bg-emerald-400 w-2 h-2" />
              </span>
              <span className="text-xs font-semibold tracking-wider text-white/80 uppercase">Now in open beta · 18+</span>
            </div>
          </div>

          {/* Main hero text */}
          <h1
            className="text-center font-black tracking-tight font-['Unbounded'] mb-4 leading-[0.95]"
            style={{ fontSize: 'clamp(2.5rem, 8vw, 5.5rem)' }}
            data-testid="hero-headline"
          >
            <span className="block">Chat with your</span>
            <span className="block bg-gradient-to-r from-fuchsia-400 via-pink-400 to-orange-300 bg-clip-text text-transparent">
              favorite anime
            </span>
            <span className="block">characters.</span>
            <span className="block italic font-light text-white/60" style={{ fontSize: 'clamp(1.5rem, 5vw, 3.5rem)' }}>
              uncensored.
            </span>
          </h1>

          <p className="text-center text-white/60 max-w-2xl mx-auto text-base sm:text-lg mt-6 mb-10" data-testid="hero-subhead">
            Roleplay with anime characters, monsters, and originals.
            <br className="hidden sm:block" />
            <span className="text-white/80 font-semibold">No filters. No judgment. No limits.</span>
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center max-w-md mx-auto">
            <Link
              to="/register"
              className="group w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-black font-black text-base shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300 whitespace-nowrap"
              data-testid="hero-cta-signup"
            >
              <Flame className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Start Chatting Free
              <ChevronRight className="w-5 h-5 -mr-1 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/login"
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] font-semibold text-base text-white/90 transition-all whitespace-nowrap"
              data-testid="hero-cta-login"
            >
              I have an account
            </Link>
          </div>

          {/* Live social proof rotator */}
          <div className="mt-10 flex justify-center min-h-[28px]">
            <div className="relative w-full max-w-md text-center">
              {SOCIAL_PROOF.map((quote, i) => (
                <p
                  key={quote}
                  className={`absolute inset-0 text-sm text-white/50 italic transition-all duration-700 ${
                    i === proofIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  }`}
                  data-testid={`social-proof-${i}`}
                >
                  {quote}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURED CHARACTERS */}
        <section className="px-5 sm:px-8 py-12 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs font-semibold text-fuchsia-400 tracking-widest uppercase mb-2">Most chatted right now</p>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight font-['Unbounded']">
                Pick your <span className="italic text-white/40">poison</span>.
              </h2>
            </div>
            <Link to="/register" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-white/60 hover:text-white" data-testid="see-all-link">
              See all 28 <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {FEATURED.map((c, i) => (
              <Link
                key={c.id}
                to="/register"
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-white/[0.04] border border-white/[0.06] hover:border-white/[0.2] transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`featured-card-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <img
                  src={c.avatar}
                  alt={c.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading={i < 4 ? 'eager' : 'lazy'}
                />
                {/* Vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                {/* Hover accent glow */}
                <div className={`absolute inset-0 bg-gradient-to-tr ${c.accent} opacity-0 group-hover:opacity-20 mix-blend-overlay transition-opacity duration-500`} />

                {/* Vibe emoji top-left */}
                <div className="absolute top-2.5 left-2.5 text-2xl drop-shadow-lg">{c.vibe}</div>

                {/* Info bottom */}
                <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                  <p className="text-xs text-white/60 font-medium mb-0.5">{c.tag}</p>
                  <h3 className="font-black text-lg sm:text-xl tracking-tight font-['Unbounded'] leading-none">{c.name}</h3>
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-white/80">
                    <MessageCircle className="w-3 h-3" />
                    <span>TAP TO CHAT</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/register" className="inline-flex items-center gap-1 text-sm font-semibold text-white/70" data-testid="see-all-mobile">
              See all 28 starter characters <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* WHY ANIMUS */}
        <section className="px-5 sm:px-8 py-16 max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-center mb-12 font-['Unbounded']">
            Built different.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10', title: 'Truly Uncensored', body: 'No corporate guardrails. Roleplay anything — from soft romance to the unholy.' },
              { icon: Sparkles, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', title: 'Memory That Stays', body: 'Your characters remember everything. Inside jokes, lore, secrets — they stick.' },
              { icon: Lock, color: 'text-cyan-400', bg: 'bg-cyan-500/10', title: 'Private Personas', body: 'Be anyone you want. Switch personas mid-chat. Nobody sees your real self.' },
            ].map((f, i) => (
              <div key={f.title} className="p-6 sm:p-8 rounded-3xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-colors" data-testid={`feature-${i}`}>
                <div className={`w-12 h-12 rounded-2xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon className={`w-6 h-6 ${f.color}`} />
                </div>
                <h3 className="font-black text-xl mb-2 font-['Unbounded']">{f.title}</h3>
                <p className="text-white/60 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* STAT STRIP */}
        <section className="px-5 sm:px-8 py-12 max-w-7xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-fuchsia-500/10 via-pink-500/5 to-orange-500/10 border border-white/[0.08] p-8 sm:p-12">
            <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
              <div data-testid="stat-characters">
                <div className="text-3xl sm:text-5xl font-black font-['Unbounded'] bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent">28+</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1 font-medium">Hand-crafted starters</div>
              </div>
              <div data-testid="stat-categories">
                <div className="text-3xl sm:text-5xl font-black font-['Unbounded'] bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent">∞</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1 font-medium">Build your own characters</div>
              </div>
              <div data-testid="stat-cost">
                <div className="text-3xl sm:text-5xl font-black font-['Unbounded'] bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent">$0</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1 font-medium">Free to start chatting</div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="px-5 sm:px-8 py-16 max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-center mb-12 font-['Unbounded']">
            Three steps. <span className="text-white/40 italic">that&apos;s it.</span>
          </h2>
          <div className="space-y-4 sm:space-y-6">
            {[
              { num: '01', title: 'Sign up free', body: 'Email + password. No card. No bullshit. 60 seconds.' },
              { num: '02', title: 'Pick a character (or create one)', body: 'Browse our 28 starters or build your own with full personality control.' },
              { num: '03', title: 'Chat. Anything.', body: 'Romance, roleplay, banter, plot, horror, anything. Zero filters.' },
            ].map((step, i) => (
              <div key={step.num} className="flex items-start gap-5 sm:gap-8 p-6 sm:p-8 rounded-2xl bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.15] transition-colors" data-testid={`step-${i}`}>
                <div className="text-4xl sm:text-6xl font-black font-['Unbounded'] bg-gradient-to-b from-fuchsia-400/80 to-fuchsia-400/20 bg-clip-text text-transparent flex-shrink-0">{step.num}</div>
                <div className="pt-1 sm:pt-2">
                  <h3 className="font-black text-xl sm:text-2xl mb-1 font-['Unbounded']">{step.title}</h3>
                  <p className="text-white/60 sm:text-lg">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="px-5 sm:px-8 py-20 max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-3 mb-6">
            <Heart className="w-6 h-6 text-pink-400 animate-pulse" />
            <Skull className="w-6 h-6 text-white/70" />
            <Flame className="w-6 h-6 text-orange-400 animate-pulse" style={{ animationDelay: '0.3s' }} />
          </div>
          <h2 className="text-4xl sm:text-6xl font-black tracking-tight font-['Unbounded'] leading-[0.95] mb-6">
            Your <span className="italic bg-gradient-to-r from-fuchsia-400 to-pink-300 bg-clip-text text-transparent">forever</span><br />
            comfort chat<br />
            <span className="text-white/40">is waiting.</span>
          </h2>
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full bg-white text-black font-black text-lg shadow-[0_0_60px_rgba(255,255,255,0.4)] hover:shadow-[0_0_80px_rgba(255,255,255,0.6)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
            data-testid="final-cta"
          >
            <Flame className="w-5 h-5" />
            Start Free Now
            <ChevronRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-xs text-white/40">No credit card · 18+ only · Cancel anytime</p>
        </section>

        {/* FOOTER */}
        <footer className="px-5 sm:px-8 py-8 border-t border-white/[0.05] max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Animus" className="w-6 h-6 object-contain opacity-60" />
              <span className="text-sm font-bold text-white/60">Animus © {new Date().getFullYear()}</span>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-2">
              <a
                href={SOCIALS.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="group w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.04] hover:bg-white/[0.12] border border-white/[0.08] hover:border-white/[0.25] text-white/60 hover:text-white transition-all duration-300 hover:scale-110"
                aria-label="Follow Animus on TikTok"
                data-testid="footer-social-tiktok"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="group w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.04] hover:bg-white/[0.12] border border-white/[0.08] hover:border-white/[0.25] text-white/60 hover:text-white transition-all duration-300 hover:scale-110"
                aria-label="Follow Animus on Instagram"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>

            <div className="flex gap-6 text-xs text-white/50">
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
              <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-login">Sign in</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
